// ---------------------------------------------------------------------------
// VT-FE hub-session settings. Asana 1218458529003876.
// Imported by src/index.js RIGHT AFTER './Util/hubSession' (before any other app
// module evaluates), so every hub request already runs with this configuration.
//
// VT-FE is public: anonymous visitors (/welcome, /property, /link, /offer, /signup,
// /hotdestinations) call the hubs too. So:
//   - logged-in agent (VT-Backend `jToken` in localStorage, written by the login in
//     store/redux/User/actions.js signIn) → hubSession exchanges it once per hub for an
//     agent session (POST <hub>/local/extranet/session/admin);
//   - anyone else, or an agent whose exchange is refused → a public session
//     (POST <hub>/local/extranet/session/public).
//
// Stored sessions win over both (hubSession.js step 1), so a public session picked up
// on /login would outlive the login. syncHubSessionsWithLogin() drops stored sessions
// whenever the login state changed since they were issued (anonymous ↔ agent, or a
// different agent). Logout already clears them (signOut → localStorage.clear()).
// ---------------------------------------------------------------------------
import { configureHubSessions, clearHubSession } from './hubSession'

const LOGIN_MARK_KEY = 'hubSessionsLogin'
const SESSIONS_KEY = 'hubSessions' // hubSession.js STORE_KEY
// An agent whose exchange was refused (expired jToken, VT-Backend down) falls back to a
// public session; retry the agent exchange at most this often, not on every page load.
const AGENT_RETRY_MS = 10 * 60 * 1000

const read = (k) => { try { return localStorage.getItem(k) } catch (e) { return null } }
const write = (k, v) => { try { localStorage.setItem(k, v) } catch (e) { /* storage blocked */ } }

const readJToken = () => {
    const t = read('jToken')
    return t && t !== 'null' && t !== 'undefined' ? t : null
}

/** Logged-in VT-FE agent = a VT-Backend token in localStorage (same check as pages/index.jsx). */
export const isAgentLoggedIn = () => !!readJToken()

// Non-reversible 32-bit FNV-1a fingerprint: tells "same login" from "different login"
// without storing any part of the token.
const fingerprint = (s) => {
    let h = 0x811c9dc5
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) }
    return (h >>> 0).toString(16)
}

/** Drop stored hub sessions that no longer match who is logged in. Never throws. */
export const syncHubSessionsWithLogin = (now = Date.now()) => {
    try {
        const jToken = readJToken()
        const login = jToken ? `agent:${fingerprint(jToken)}` : 'anon'
        let mark = null
        try { mark = JSON.parse(read(LOGIN_MARK_KEY) || 'null') } catch (e) { mark = null }
        if (!mark || mark.login !== login) {
            clearHubSession()
            write(LOGIN_MARK_KEY, JSON.stringify({ login, at: now }))
            return
        }
        if (!jToken || now - (Number(mark.at) || 0) < AGENT_RETRY_MS) return
        let sessions = {}
        try { sessions = JSON.parse(read(SESSIONS_KEY) || '{}') || {} } catch (e) { sessions = {} }
        const publicOrigins = Object.keys(sessions).filter((o) => sessions[o] && sessions[o].role === 'public')
        if (!publicOrigins.length) return
        publicOrigins.forEach((origin) => clearHubSession(origin))
        write(LOGIN_MARK_KEY, JSON.stringify({ login, at: now }))
    } catch (e) { /* never block the app on this */ }
}

configureHubSessions({ publicSessions: true, isAdminLogin: isAgentLoggedIn })
syncHubSessionsWithLogin()
